import {useEffect} from "react";
import {App as AntdApp, Tag} from "antd";
import {useForm, Controller} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {ModalForm} from "~/components";
import {InputField, SelectField} from "~/components/Forms";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {useGetProjectsQuery} from "~/reduxs/api/projectApiSlice";
import {useAddKeywordItemsMutation} from "~/reduxs/api/keywordApiSlice";

const schema = Yup.object().shape({
	project_id: Yup.number().typeError('Vui lòng chọn dự án').required('Vui lòng chọn dự án'),
});

/**
 * Modal lưu từ khoá trending vào Kho từ khoá — kho thuộc từng dự án nên user chọn
 * dự án đích tại đây (Google Trends là tool độc lập, không gắn dự án). Tái dùng
 * endpoint addKeywordItems sẵn có với source 'trends' + meta (traffic/geo/tin đầu).
 */
function TrendsSaveModal({open, keywords = [], geo = 'VN', onCancel, onSaved}) {

	const {notification} = AntdApp.useApp();
	const {data: projects = [], isFetching: loadingProjects} = useGetProjectsQuery(undefined, {skip: !open});
	const [addItems, {isLoading: saving}] = useAddKeywordItemsMutation();

	const {control, handleSubmit, reset, formState: {errors}} = useForm({
		defaultValues: {project_id: undefined, note: ''},
		resolver: yupResolver(schema),
	});

	useEffect(() => {
		if (open) reset({project_id: undefined, note: ''});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const onSubmit = async ({project_id, note}) => {
		const items = keywords.map((it) => ({
			keyword: it.keyword,
			source: 'trends',
			...(note?.trim() ? {note: note.trim()} : {}),
			meta: {
				traffic: it.approx_traffic || '',
				traffic_min: it.traffic_min || 0,
				geo,
				news_url: it.news?.[0]?.url || '',
			},
		}));

		if (!items.length) return;

		try {
			const res = await addItems({projectId: project_id, items}).unwrap();
			if (res?.added > 0) {
				notification.success({message: 'Đã lưu vào kho', description: `${res.added} từ khoá mới${res.skipped ? ` (${res.skipped} đã có sẵn)` : ''}.`});
			} else {
				notification.info({message: 'Đã có', description: 'Các từ khoá này đã có trong kho của dự án.'});
			}
			onSaved?.();
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không lưu được từ khoá')});
		}
	};

	return (
		<ModalForm
			open={open}
			icon="fa-light fa-bookmark"
			title="Lưu từ khoá trending vào kho"
			subtitle="Kho từ khoá thuộc từng dự án — chọn dự án đích; từ khoá trùng sẽ tự bỏ qua"
			onCancel={onCancel}
			onOk={handleSubmit(onSubmit)}
			okText="Lưu vào kho"
			loading={saving}
		>
			<div className="form-group">
				<label>Từ khoá sẽ lưu ({keywords.length})</label>
				<div style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
					{keywords.map((it) => (
						<Tag key={it.hash} color="blue">{it.keyword}{it.approx_traffic ? ` · ${it.approx_traffic}` : ''}</Tag>
					))}
				</div>
			</div>
			<Controller control={control} name="project_id" render={({field}) => (
				<SelectField label="Dự án" loading={loadingProjects} errors={errors}
					options={projects.map((p) => ({value: p.id, label: p.name}))} {...field} />
			)} />
			<Controller control={control} name="note" render={({field}) => (
				<InputField label="Ghi chú (tuỳ chọn)" placeholder="VD: trending VN 05/07" errors={errors} {...field} />
			)} />
		</ModalForm>
	);
}

export default TrendsSaveModal;
